import Alpine from 'alpinejs';
import axios from '../../config/axios.js';

function blankIngredient() {
  return { id: '', label: '', icon: '', required: false, decoy: false };
}

function blankForm(sortOrder = 1) {
  return {
    id: null,
    code: '',
    title: '',
    subtitle: '',
    description: '',
    station_title: 'Workshop INKO',
    animation_key: '',
    ingredients: [
      { id: 'ink_cyan', label: 'Tinta Cyan', icon: 'ink_cyan', required: true, decoy: false },
      { id: 'snack', label: 'Camilan', icon: 'snack', required: false, decoy: true },
    ],
    ui_meta: {
      hint: 'Masukkan tinta & bahan yang benar, lalu tekan Start!',
      start_button_label: 'Start!',
      work_caption: 'INKO sedang bekerja...',
      success_message: 'Hebat! INKO bilang terima kasih 🎉',
    },
    points_reward: 20,
    xp_reward: 30,
    work_duration_ms: 8000,
    is_active: true,
    sort_order: sortOrder,
  };
}

function createKasirMascotQuests() {
  return {
    quests: [],
    ingredientPresets: [],
    todayAssigned: 0,
    todayCompleted: 0,
    loading: false,
    saving: false,
    modal: false,
    editing: false,
    form: blankForm(),

    init() {
      try {
        const cfg = JSON.parse(this.$el.getAttribute('data-config') || '{}');
        this.quests = cfg.quests || [];
        this.todayAssigned = cfg.todayAssigned || 0;
        this.todayCompleted = cfg.todayCompleted || 0;
        this.ingredientPresets = cfg.ingredientPresets || [];
      } catch (e) {
        console.error('kasirMascotQuests config', e);
      }
    },

    openCreate() {
      this.editing = false;
      this.form = blankForm((this.quests.length || 0) + 1);
      this.modal = true;
    },

    openEdit(q) {
      this.editing = true;
      const meta = q.ui_meta || {};
      this.form = {
        id: q.id,
        code: q.code || '',
        title: q.title || '',
        subtitle: q.subtitle || '',
        description: q.description || '',
        station_title: q.station_title || 'Workshop INKO',
        animation_key: q.animation_key || '',
        ingredients: (q.ingredients || []).map((i) => ({
          id: i.id || '',
          label: i.label || '',
          icon: i.icon || i.id || '',
          required: !!i.required,
          decoy: !!i.decoy,
        })),
        ui_meta: {
          hint: meta.hint || '',
          start_button_label: meta.start_button_label || '',
          work_caption: meta.work_caption || '',
          success_message: meta.success_message || '',
        },
        points_reward: q.points_reward ?? 20,
        xp_reward: q.xp_reward ?? 30,
        work_duration_ms: q.work_duration_ms ?? 8000,
        is_active: !!q.is_active,
        sort_order: q.sort_order ?? 0,
      };
      if (!this.form.ingredients.length) {
        this.form.ingredients = [blankIngredient()];
      }
      this.modal = true;
    },

    closeModal() {
      this.modal = false;
    },

    addIngredient() {
      this.form.ingredients.push(blankIngredient());
    },

    addPreset(p) {
      if (this.form.ingredients.some((i) => i.id === p.id)) {
        window.toast?.('Bahan sudah ada.', 'error');
        return;
      }
      this.form.ingredients.push({
        id: p.id,
        label: p.label,
        icon: p.id,
        required: !['snack', 'phone'].includes(p.id),
        decoy: ['snack', 'phone'].includes(p.id),
      });
    },

    removeIngredient(idx) {
      this.form.ingredients.splice(idx, 1);
    },

    payload() {
      return {
        code: String(this.form.code || '').trim() || null,
        title: String(this.form.title || '').trim(),
        subtitle: String(this.form.subtitle || '').trim() || null,
        description: String(this.form.description || '').trim() || null,
        station_title: String(this.form.station_title || '').trim() || 'Workshop INKO',
        animation_key: String(this.form.animation_key || '').trim() || null,
        ingredients: (this.form.ingredients || [])
          .filter((i) => String(i.label || '').trim())
          .map((i) => ({
            id: String(i.id || i.label || '').trim(),
            label: String(i.label || '').trim(),
            icon: String(i.icon || i.id || '').trim() || undefined,
            required: !!i.required,
            decoy: !!i.decoy && !i.required,
          })),
        ui_meta: {
          hint: String(this.form.ui_meta?.hint || '').trim() || null,
          start_button_label: String(this.form.ui_meta?.start_button_label || '').trim() || null,
          work_caption: String(this.form.ui_meta?.work_caption || '').trim() || null,
          success_message: String(this.form.ui_meta?.success_message || '').trim() || null,
        },
        points_reward: Number(this.form.points_reward) || 0,
        xp_reward: Number(this.form.xp_reward) || 0,
        work_duration_ms: Number(this.form.work_duration_ms) || 8000,
        is_active: !!this.form.is_active,
        sort_order: Number(this.form.sort_order) || 0,
      };
    },

    async save() {
      const body = this.payload();
      if (!body.title) {
        window.toast?.('Judul wajib diisi.', 'error');
        return;
      }
      if (!body.ingredients.length) {
        window.toast?.('Minimal satu bahan.', 'error');
        return;
      }
      if (!body.ingredients.some((i) => i.required)) {
        window.toast?.('Minimal satu bahan wajib.', 'error');
        return;
      }

      this.saving = true;
      try {
        if (this.editing && this.form.id) {
          const { data } = await axios.put(`/api/v1/kasir/engagement/mascot-quests/${this.form.id}`, body);
          const updated = data.data;
          const idx = this.quests.findIndex((q) => q.id === updated.id);
          if (idx >= 0) this.quests[idx] = updated;
          else this.quests.push(updated);
          window.toast?.(data.message || 'Quest diperbarui.', 'success');
        } else {
          const { data } = await axios.post('/api/v1/kasir/engagement/mascot-quests', body);
          this.quests.push(data.data);
          this.quests.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
          window.toast?.(data.message || 'Quest ditambahkan.', 'success');
        }
        this.closeModal();
      } catch (e) {
        const msg = e?.response?.data?.message
          || e?.response?.data?.errors?.ingredients?.[0]
          || e?.response?.data?.errors?.title?.[0]
          || 'Gagal menyimpan quest.';
        window.toast?.(msg, 'error');
      } finally {
        this.saving = false;
      }
    },

    async remove(q) {
      if (!window.confirm(`Hapus quest "${q.title}"?`)) return;
      try {
        await axios.delete(`/api/v1/kasir/engagement/mascot-quests/${q.id}`);
        this.quests = this.quests.filter((row) => row.id !== q.id);
        window.toast?.('Quest dihapus.', 'success');
      } catch (e) {
        window.toast?.(e?.response?.data?.message || 'Gagal menghapus.', 'error');
      }
    },
  };
}

Alpine.data('kasirMascotQuests', createKasirMascotQuests);
